import React from 'react';
import { getCurrentPracticeId, getCurrentUserInfo } from '../lib/api';
import { db } from "../lib/db";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  async componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    try {
      const pid = await getCurrentPracticeId();
      const { uid } = await getCurrentUserInfo();
      await db.from('error_log').insert([{
        message:         error?.message || String(error),
        stack:           error?.stack   || null,
        component_stack: errorInfo?.componentStack || null,
        boundary:        this.props.name || 'unknown',
        url:             window.location.href,
        occurred_at:     new Date().toISOString(),
        practice_id:     pid || 'unknown',
        user_id:         uid || null,
      }]);
    } catch (_) {
      // Never throw from an error boundary
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const title   = this.props.title   || 'Something went wrong';
    const message = this.props.message || 'This section encountered an error. The rest of the app is still running.';

    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: 280, padding: '40px 24px', gap: 16,
        background: 'var(--navy-2)', borderRadius: 12, margin: 20,
        fontFamily: 'var(--font, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif)',
      }}>
        <div style={{ fontSize: 36 }}>⚠️</div>

        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-1)' }}>{title}</div>

        <div style={{ fontSize: 13, color: 'var(--text-2)', textAlign: 'center', maxWidth: 400, lineHeight: 1.6 }}>
          {message}
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <button
            onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
            style={{
              padding: '9px 20px', background: 'var(--teal)', color: '#fff',
              border: 'none', borderRadius: 8, cursor: 'pointer',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
            }}
          >
            Try Again
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '9px 20px', background: 'var(--navy-3)', color: 'var(--text-1)',
              border: '1px solid var(--border-2)', borderRadius: 8, cursor: 'pointer',
              fontSize: 13, fontWeight: 500, fontFamily: 'inherit',
            }}
          >
            Reload App
          </button>
        </div>

        {process.env.NODE_ENV === 'development' && this.state.error && (
          <details style={{ marginTop: 12, textAlign: 'left', width: '100%', maxWidth: 600 }}>
            <summary style={{ color: 'var(--text-3)', cursor: 'pointer', fontSize: 12 }}>
              Error details (dev only)
            </summary>
            <pre style={{
              marginTop: 8, padding: 12, background: 'var(--navy)',
              borderRadius: 8, fontSize: 11, overflow: 'auto',
              color: '#ff6b6b', lineHeight: 1.5, whiteSpace: 'pre-wrap',
            }}>
              {this.state.error.toString()}
              {this.state.errorInfo?.componentStack}
            </pre>
          </details>
        )}
      </div>
    );
  }
}
